/**
 * Core request helpers: config validation, auth headers, URL builders.
 * Handler calls use portfolio/org from runtime workspace (see callHandler).
 */

import { config } from '../../config';
import { getAccessToken } from '../../components/auth/getAccessToken';
import { getWorkspace } from '../../workspace/workspaceStore';

/** Scheduler URL key. Local may be `arbitiumlab`; production is `arbitium`. */
export const ARBITIUM_EXTENSION = config.arbitiumExtension;

export function requireConfig(): { apiBase: string; portfolio: string; org: string } {
  const apiBase = (config.apiBase || '').trim();
  const { portfolio, org } = getWorkspace();
  if (!apiBase || !portfolio || !org) {
    throw new Error(
      'Missing API config (apiBase, portfolio, org). Set REACT_APP_API_BASE / REACT_APP_PORTFOLIO and select an org.'
    );
  }
  return { apiBase, portfolio: portfolio.trim(), org: org.trim() };
}

export async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await getAccessToken();
  if (!token) throw new Error('Sign in required');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

/** Base payload for handler calls: portfolio and org from runtime workspace. */
export function getPayloadBase(): { portfolio: string; org: string } {
  const { portfolio, org } = requireConfig();
  return { portfolio, org };
}

export interface DataQueryOptions {
  limit?: number;
  lastkey?: string;
  sort?: string;
  all?: boolean;
  refresh?: boolean;
}

/** URL for GET /_data/{portfolio}/{org}/{ring} */
export function buildDataUrl(ring: string, options?: DataQueryOptions): string {
  const { apiBase, portfolio, org } = requireConfig();
  const url = new URL(`${apiBase}/_data/${portfolio}/${org}/${ring}`);
  if (options?.limit != null) url.searchParams.set('limit', String(options.limit));
  if (options?.lastkey) url.searchParams.set('lastkey', options.lastkey);
  if (options?.sort) url.searchParams.set('sort', options.sort);
  if (options?.all) url.searchParams.set('all', '1');
  if (options?.refresh) url.searchParams.set('refresh', '1');
  return url.toString();
}

/** URL for GET/PUT/DELETE /_data/{portfolio}/{org}/{ring}/{id} */
export function buildDataDocUrl(ring: string, id: string): string {
  const { apiBase, portfolio, org } = requireConfig();
  return `${apiBase}/_data/${portfolio}/${org}/${ring}/${id}`;
}

/** URL for POST /_schd/{portfolio}/{org}/call/{extension}/{handler} or .../call/{extension}/{handler}/{subhandler} */
export function buildCallUrl(extension: string, handler: string, subhandler?: string): string {
  const { apiBase, portfolio, org } = requireConfig();
  const base = `${apiBase}/_schd/${portfolio}/${org}/call/${extension}/${handler}`;
  return subhandler ? `${base}/${subhandler}` : base;
}
